import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import { getThemeById } from '../../config/themes'
import { CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES, formatCustomerSlipCancelWindowText, getRoundImage, lotteryService, type LotteryRound } from '../../services/lotteryService'
import * as authService from '../../services/authService'
import * as walletService from '../../services/walletService'
import { useLotteryCountdown } from '../../hooks/useLotteryCountdown'
import { getRuntimeTenantIdSync } from '../../lib/runtimeTenant'
import {
  applyResolvedSetPackage,
  buildFallbackSetPackageFromState,
  buildInitialSetPackage,
  buildSetPackageFromState,
  isHydratedSetPackage,
  matchSetPackageBySelection,
  mergeSetLottoState,
  resolveSelectedSetPackageCode,
  resolveSetPackageFixedPrice,
  type SetLottoBettingPackage,
} from '../../utils/setLottoPackageUtils'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import type { BettingPackage } from './ModernLobbyHuayLotteryTermsPage'
import RulesGuideModal from '../../components/RulesGuideModal'
import './ModernLobbyHuaySetLottoGamePage.css'

type SetLottoPageState = {
  backTo?: string
  backLabel?: string
  title?: string
  logo?: string
  round?: string
  time?: string
  close?: string
  roundFromApi?: LotteryRound
  package?: BettingPackage
  packageCode?: string
  fixedPrice?: number
}

const PRESET_SETS = [1, 2, 3, 4, 5, 10]
const DIGIT_COUNT = 4
const DEFAULT_SET_COUNT = 1
const SET_FOUR_DIGIT_PRIZE_CODE_PREFERENCE = ['4_DIGIT_EXACT', '4_DIGIT_TOP', 'FIRST_PRIZE'] as const
const SET_PACKAGE_COLUMNS = [
  { key: 'large', codeHints: ['LARGE', 'ใหญ่'], label: 'ชุดใหญ่' },
  { key: 'medium', codeHints: ['MEDIUM', 'กลาง'], label: 'ชุดกลาง' },
  { key: 'small', codeHints: ['SMALL', 'เล็ก'], label: 'ชุดเล็ก' },
] as const
const SET_PAYOUT_ROWS = [
  { label: '4 ตัวตรง', large: '100,000', medium: '50,000', small: '25,000' },
  { label: '3 ตัวตรง', large: '35,000', medium: '17,500', small: '8,000' },
  { label: '4 ตัวโต๊ด', large: '5,000', medium: '2,500', small: '1,250' },
  { label: '3 ตัวโต๊ด', large: '3,500', medium: '1,750', small: '800' },
  { label: '2 ตัวหน้า', large: '1,600', medium: '700', small: '350' },
  { label: '2 ตัวหลัง', large: '1,600', medium: '700', small: '350' },
]

const SET_PRIZE_LABEL_BY_CODE: Record<string, string> = {
  '4_DIGIT_EXACT': '4 ตัวตรง',
  '4_DIGIT_TOP': '4 ตัวตรง',
  FIRST_PRIZE: '4 ตัวตรง',
  '3_DIGIT_TOP': '3 ตัวตรง',
  '4_DIGIT_TOD': '4 ตัวโต๊ด',
  '3_DIGIT_TOD': '3 ตัวโต๊ด',
  '2_DIGIT_FRONT': '2 ตัวหน้า',
  '2_DIGIT_TOP': '2 ตัวหน้า',
  '2_DIGIT_BACK': '2 ตัวหลัง',
  '2_DIGIT_BOTTOM': '2 ตัวหลัง',
}

const SET_PRIZE_DISPLAY_ORDER = [
  '4 ตัวตรง',
  '3 ตัวตรง',
  '4 ตัวโต๊ด',
  '3 ตัวโต๊ด',
  '2 ตัวหน้า',
  '2 ตัวหลัง',
]

function formatCloseTime(betEndTime: string): string {
  return new Date(betEndTime).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function resolveSetFourDigitPrizeTypeCode(selectedPackage?: SetLottoBettingPackage): string {
  const supportedCodes = new Set<string>()

  for (const config of selectedPackage?.prizeConfigs ?? []) {
    const code = String(config.prizeTypeCode || '').trim().toUpperCase()
    if (code) supportedCodes.add(code)
  }

  for (const config of selectedPackage?.setConfig?.includedPrizeTypes ?? []) {
    const code = String(config.prizeTypeCode || '').trim().toUpperCase()
    if (code) supportedCodes.add(code)
  }

  return SET_FOUR_DIGIT_PRIZE_CODE_PREFERENCE.find((code) => supportedCodes.has(code)) ?? '4_DIGIT_TOP'
}

function formatSetPayoutAmount(value?: number | null): string {
  const amount = Number(value || 0)
  return amount > 0 ? amount.toLocaleString('th-TH') : '-'
}

function getSetPackageColumnKey(pkg?: SetLottoBettingPackage): typeof SET_PACKAGE_COLUMNS[number]['key'] | null {
  const combined = `${pkg?.code || ''} ${pkg?.name || ''}`.toUpperCase()
  const matched = SET_PACKAGE_COLUMNS.find((column) =>
    column.codeHints.some((hint) => combined.includes(hint.toUpperCase()))
  )
  return matched?.key || null
}

function buildSetPayoutRows(selectedPackage?: SetLottoBettingPackage) {
  const packageColumn = getSetPackageColumnKey(selectedPackage)
  if (!selectedPackage?.prizeConfigs?.length || !packageColumn) {
    return SET_PAYOUT_ROWS
  }

  const valuesByLabel = new Map<string, string>()
  for (const config of selectedPackage.prizeConfigs) {
    if ((config as { isActive?: boolean }).isActive === false) continue
    const code = String(config.prizeTypeCode || '').trim().toUpperCase()
    const label = SET_PRIZE_LABEL_BY_CODE[code] || config.prizeTypeName || code
    if (!label || valuesByLabel.has(label)) continue
    valuesByLabel.set(label, formatSetPayoutAmount(config.payoutRate))
  }

  return SET_PRIZE_DISPLAY_ORDER.map((label) => {
    const fallback = SET_PAYOUT_ROWS.find((row) => row.label === label)
    return {
      label,
      large: packageColumn === 'large' ? valuesByLabel.get(label) || fallback?.large || '-' : fallback?.large || '-',
      medium: packageColumn === 'medium' ? valuesByLabel.get(label) || fallback?.medium || '-' : fallback?.medium || '-',
      small: packageColumn === 'small' ? valuesByLabel.get(label) || fallback?.small || '-' : fallback?.small || '-',
    }
  })
}

function translateSetBetError(message?: string, errors?: Array<{ reason?: string; number?: string }>): string {
  const rawMessage = String(message || '').trim()
  const firstError = Array.isArray(errors) ? errors.find((item) => item?.reason || item?.number) : null
  const reason = String(firstError?.reason || '').trim()
  const lowerReason = reason.toLowerCase()
  const lowerMessage = rawMessage.toLowerCase()
  const number = String(firstError?.number || '').trim()

  if (/not available in package|ไม่รองรับในแพ็กเกจ|betting package not found|package not found|ไม่พบแพ็กเกจการเดิมพัน/i.test(rawMessage)) {
    return 'แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่'
  }

  if (
    lowerReason.includes('number is blocked or full') ||
    lowerReason.includes('number has reached its betting limit') ||
    lowerReason.includes('would exceed total betting limit') ||
    lowerMessage.includes('some bet items are no longer valid')
  ) {
    return number
      ? `เลข ${number} ไม่สามารถซื้อได้แล้ว กรุณาลบออกจากรายการแล้วลองใหม่`
      : 'มีบางรายการไม่สามารถซื้อได้แล้ว กรุณาลบเลขที่มีปัญหาออกแล้วลองใหม่'
  }

  if (lowerReason.includes('not active')) return 'รายการนี้ปิดรับแทงแล้ว กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('prize type not found')) return 'ไม่พบประเภทรางวัลของรายการนี้ กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('invalid number length')) return 'รูปแบบเลขไม่ถูกต้อง กรุณาลบรายการนี้แล้วเลือกใหม่'

  return rawMessage || 'ส่งโพยไม่สำเร็จ'
}

const ModernLobbyHuaySetLottoGamePage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { themeConfig, assetConfig } = useOutletContext<LobbyHuayOutletContext>()
  const tenantId = getRuntimeTenantIdSync()
  const baseUrl = (import.meta.env as { VITE_API_BASE_URL?: string }).VITE_API_BASE_URL || 'http://localhost:8081'
  const rawState = (location.state ?? {}) as SetLottoPageState
  const navState = useMemo(
    () => mergeSetLottoState({
      round: rawState.roundFromApi,
      package: rawState.package,
      packageCode: rawState.packageCode,
      fixedPrice: rawState.fixedPrice,
      backTo: rawState.backTo,
      backLabel: rawState.backLabel,
    }),
    [location.state]
  )
  const round = navState.round ?? rawState.roundFromApi
  const initialPackage = buildInitialSetPackage(navState, round)
  const needsPackageHydration = Boolean(round?.huayId && !isHydratedSetPackage(initialPackage))
  const [resolvedPackage, setResolvedPackage] = useState(initialPackage)
  const [resolvingPackage, setResolvingPackage] = useState(needsPackageHydration)
  const [packageHydrationFailed, setPackageHydrationFailed] = useState(false)
  const selectedPackage = resolvedPackage
  const packageCode =
    resolveSelectedSetPackageCode(navState, round, selectedPackage)
    || selectedPackage?.code
    || navState.packageCode
    || ''
  const fixedPrice = useMemo(
    () => resolveSetPackageFixedPrice(selectedPackage, navState),
    [selectedPackage, navState]
  )
  const fixedDiscount = selectedPackage?.setConfig?.discount ?? navState.package?.setConfig?.discount ?? 0
  const backTo = navState.backTo ?? rawState.backTo ?? '/lobby-huay/lottery-terms'
  const backLabel = navState.backLabel ?? rawState.backLabel ?? 'กลับเลือกแพ็กเกจ'
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [isLoadingWallet, setIsLoadingWallet] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [cancelWindowMinutes, setCancelWindowMinutes] = useState(CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES)
  const [numbers, setNumbers] = useState<string[]>(Array.from({ length: DIGIT_COUNT }, () => ''))
  const [setCountInput, setSetCountInput] = useState(String(DEFAULT_SET_COUNT))
  const [submitting, setSubmitting] = useState(false)
  const walletBalance = walletService.getDisplayBalance(walletData)

  const getAssetUrl = useMemo(() => {
    const folder = getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue'
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${folder}` : `/${folder}`
    return (path: string) => base + (path.startsWith('/') ? path : `/${path}`)
  }, [themeConfig.themeId, assetConfig.CDN_BASE_URL])

  const displayLogo = useMemo(() => {
    if (round?.huayImageURL && /^https?:\/\//i.test(round.huayImageURL)) return round.huayImageURL
    const path = round ? getRoundImage(round) : rawState.logo ?? '/img/logo.png'
    if (/^https?:\/\//i.test(path)) return path
    return getAssetUrl(path.startsWith('/') ? path : `/${path}`)
  }, [round, rawState.logo, getAssetUrl])

  const displayTitle = round?.huayName ?? rawState.title ?? 'หวยชุด'
  const displayTime = useLotteryCountdown(round?.betEndTime, rawState.time ?? '24 ชั่วโมง')
  const displayClose = round ? formatCloseTime(round.betEndTime) : (rawState.close ?? '')
  const packageLabel = selectedPackage?.name ?? 'ชุดเล็ก'
  const setCount = useMemo(() => {
    const trimmed = setCountInput.trim()
    if (!trimmed) return DEFAULT_SET_COUNT
    const parsed = Number.parseInt(trimmed, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }, [setCountInput])
  const totalBet = fixedPrice > 0 ? setCount * fixedPrice : 0
  const setFourDigitPrizeTypeCode = useMemo(
    () => resolveSetFourDigitPrizeTypeCode(selectedPackage),
    [selectedPackage]
  )
  const setPayoutRows = useMemo(() => buildSetPayoutRows(selectedPackage), [selectedPackage])
  const activePackageColumn = useMemo(() => getSetPackageColumnKey(selectedPackage), [selectedPackage])

  const goBackToTerms = () => {
    navigate(backTo, {
      state: {
        title: rawState.title,
        logo: rawState.logo,
        round: round ?? rawState.round,
        time: rawState.time,
        close: rawState.close,
      },
    })
  }

  useEffect(() => {
    let isMounted = true
    void lotteryService.getCustomerSlipCancelWindowMinutes().then((minutes) => {
      if (isMounted) setCancelWindowMinutes(minutes)
    })
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (!round) {
      navigate('/lobby-huay/listall', { replace: true })
    }
  }, [navigate, round])

  useEffect(() => {
    const nextPackage = buildSetPackageFromState(navState)
    if (!nextPackage) return
    setResolvedPackage(nextPackage)
    setPackageHydrationFailed(false)
    if (isHydratedSetPackage(nextPackage)) {
      setResolvingPackage(false)
    }
  }, [navState.package, navState.packageCode, navState.fixedPrice])

  useEffect(() => {
    if (!round?.huayId || !tenantId) return

    if (isHydratedSetPackage(selectedPackage)) {
      setResolvingPackage(false)
      setPackageHydrationFailed(false)
      return
    }

    const lookupCode = packageCode || navState.packageCode || navState.package?.code
    const lookupPrice = resolveSetPackageFixedPrice(navState.package, navState)

    let cancelled = false
    const hydratePackage = async () => {
      setResolvingPackage(true)
      setPackageHydrationFailed(false)
      try {
        const accessToken = authService.getAccessToken()
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantId,
        }
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`

        const response = await axios.get(
          `${baseUrl}/api/v1/lottery/tenant/${tenantId}/huays/${round.huayId}/packages`,
          { headers, withCredentials: true }
        )
        const packages = response.data?.data?.packages || []
        const matched = matchSetPackageBySelection(packages, lookupCode, lookupPrice)
        if (!cancelled && matched) {
          const hydratedPackage = applyResolvedSetPackage(matched, navState)
          const hydratedPrice = resolveSetPackageFixedPrice(hydratedPackage, navState)
          setResolvedPackage(hydratedPackage)
          mergeSetLottoState({
            round,
            package: hydratedPackage,
            packageCode: hydratedPackage.code,
            fixedPrice: hydratedPrice > 0 ? hydratedPrice : undefined,
            backTo: navState.backTo,
            backLabel: navState.backLabel,
          })
          if (hydratedPrice <= 0) {
            const fallbackPackage = buildFallbackSetPackageFromState(navState, round)
            if (fallbackPackage && isHydratedSetPackage(fallbackPackage)) {
              setResolvedPackage(fallbackPackage)
              setPackageHydrationFailed(false)
            } else {
              setPackageHydrationFailed(true)
            }
          }
        } else if (!cancelled) {
          const fallbackPackage = buildFallbackSetPackageFromState(navState, round)
          if (fallbackPackage && isHydratedSetPackage(fallbackPackage)) {
            setResolvedPackage(fallbackPackage)
            mergeSetLottoState({
              round,
              package: fallbackPackage,
              packageCode: fallbackPackage.code,
              fixedPrice: resolveSetPackageFixedPrice(fallbackPackage, navState),
              backTo: navState.backTo,
              backLabel: navState.backLabel,
            })
            setPackageHydrationFailed(false)
          } else {
            setPackageHydrationFailed(true)
          }
        }
      } catch {
        if (!cancelled) {
          const fallbackPackage = buildFallbackSetPackageFromState(navState, round)
          if (fallbackPackage && isHydratedSetPackage(fallbackPackage)) {
            setResolvedPackage(fallbackPackage)
            setPackageHydrationFailed(false)
          } else {
            setPackageHydrationFailed(true)
          }
        }
      } finally {
        if (!cancelled) setResolvingPackage(false)
      }
    }

    void hydratePackage()
    return () => {
      cancelled = true
    }
  }, [round?.huayId, tenantId, packageCode, navState, selectedPackage, baseUrl])

  useEffect(() => {
    walletService.getWallet().then((res) => {
      if (res.success && res.data) setWalletData(res.data)
      setIsLoadingWallet(false)
    })
  }, [])

  const handleNumberInput = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    setNumbers((prev) => {
      const next = [...prev]
      next[index] = digit
      return next
    })
  }

  const handleNumberButton = (digit: string) => {
    const nextIndex = numbers.findIndex((value) => value === '')
    if (nextIndex !== -1) {
      handleNumberInput(nextIndex, digit)
    }
  }

  const handleBackspace = () => {
    for (let index = numbers.length - 1; index >= 0; index -= 1) {
      if (numbers[index] !== '') {
        handleNumberInput(index, '')
        return
      }
    }
  }

  const handleRandom = () => {
    setNumbers(Array.from({ length: DIGIT_COUNT }, () => Math.floor(Math.random() * 10).toString()))
  }

  const handleClear = () => {
    setNumbers(Array.from({ length: DIGIT_COUNT }, () => ''))
    setSetCountInput(String(DEFAULT_SET_COUNT))
  }

  const handleSetSelection = (count: number) => {
    setSetCountInput(String(count))
  }

  const handleSetCountChange = (value: string) => {
    setSetCountInput(value.replace(/\D/g, '').slice(0, 5))
  }

  const handleSetCountDelta = (delta: number) => {
    const next = Math.max(DEFAULT_SET_COUNT, (Number.parseInt(setCountInput || '0', 10) || 0) + delta)
    setSetCountInput(String(next))
  }

  const handleSubmitBet = async () => {
    const numberStr = numbers.join('').trim()
    if (numberStr.length < 4) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาระบุตัวเลขอย่างน้อย 4 หลัก' })
      return
    }
    if (totalBet <= 0) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาเลือกจำนวนชุด' })
      return
    }
    if (resolvingPackage) {
      await Swal.fire({ icon: 'info', title: 'กำลังโหลดแพ็กเกจ กรุณารอสักครู่' })
      return
    }
    if (!round?._id || !packageCode || fixedPrice <= 0 || !selectedPackage?.setConfig) {
      await Swal.fire({ icon: 'warning', title: 'ไม่พบข้อมูลรอบหวยหรือแพ็กเกจ' })
      goBackToTerms()
      return
    }
    if (totalBet > walletBalance) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดเงินไม่เพียงพอ',
        html: `ยอดเครดิตคงเหลือ <strong>${walletService.formatCurrency(walletBalance)}</strong> บาท น้อยกว่ายอดที่ต้องการแทง <strong>${walletService.formatCurrency(totalBet)}</strong> บาท`,
        confirmButtonText: 'ตกลง',
      })
      return
    }

    let userId: string | null = null
    let username: string | undefined
    const authToken = authService.getAccessToken()
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]))
        userId = payload.sub ?? payload.id ?? payload.userId ?? null
        username = payload.username
      } catch {
        // ignore malformed token
      }
    }
    if (!username) {
      try {
        username = authService.getCurrentUser()?.username
      } catch {
        // ignore malformed user
      }
    }
    if (!userId) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาเข้าสู่ระบบใหม่อีกครั้ง' })
      return
    }

    try {
      setSubmitting(true)
      const result = await lotteryService.placeBet(
        userId,
        round._id,
        packageCode,
        [{ prizeTypeCode: setFourDigitPrizeTypeCode, number: numberStr.slice(0, 4), amount: totalBet }],
        'web',
        username
      )
      const slipId = (result as { data?: { slipId?: string } })?.data?.slipId
      if (result?.success && slipId) {
        walletService.getWallet().then((res) => {
          if (res.success && res.data) setWalletData(res.data)
        })
        const response = await Swal.fire({
          icon: 'success',
          title: 'ส่งโพยเรียบร้อย',
          text: 'โพยของคุณได้รับการบันทึกแล้ว',
          confirmButtonText: 'ดูโพย',
          showCancelButton: true,
          cancelButtonText: 'แทงต่อ',
          confirmButtonColor: '#16a34a',
          cancelButtonColor: '#ea580c',
          reverseButtons: true,
          customClass: {
            popup: 'htd-swal-popup htd-swal-popup--bright htd-bet-success-swal',
            title: 'htd-swal-title htd-bet-success-swal__title',
            htmlContainer: 'htd-swal-html htd-bet-success-swal__html',
            actions: 'htd-swal-actions htd-bet-success-swal__actions',
            confirmButton: 'htd-swal-confirm htd-bet-success-swal__confirm',
            cancelButton: 'htd-swal-cancel htd-bet-success-swal__cancel',
          },
        })
        if (response.isConfirmed) {
          navigate(`/lobby-huay/slip/detail/${slipId}`)
        } else {
          handleClear()
        }
      } else {
        await Swal.fire({ icon: 'success', title: 'ส่งโพยเรียบร้อย' })
        handleClear()
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      const responseData = err.response?.data as { message?: string; errors?: Array<{ reason?: string; number?: string }> } | undefined
      const message = responseData?.message || err.message || 'ส่งโพยไม่สำเร็จ'
      const translatedMessage = translateSetBetError(message, responseData?.errors)
      if (translatedMessage === 'แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่') {
        await Swal.fire({ icon: 'warning', title: 'แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่' })
        goBackToTerms()
        return
      }
      await Swal.fire({ icon: 'error', title: translatedMessage })
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit =
    !submitting
    && !resolvingPackage
    && !packageHydrationFailed
    && totalBet > 0
    && numbers.join('').length >= 4
    && fixedPrice > 0

  return (
    <>
      <div className="htd-set">
        <div className="htd-set__top">
          <button type="button" className="htd-set__back" onClick={goBackToTerms}>
            <i className="fas fa-chevron-left" aria-hidden />
            {backLabel}
          </button>
          <button type="button" className="htd-set__rules" onClick={() => setShowModal(true)}>
            <i className="fas fa-book-open" aria-hidden />
            กติกา & วิธีเล่น
          </button>
        </div>

        <div className="htd-set__round">
          <div className="htd-set__logo">
            <img src={displayLogo} alt="" />
          </div>
          <div className="htd-set__meta">
            <h1 className="htd-set__title" title={displayTitle}>{displayTitle}</h1>
            {displayClose ? <p className="htd-set__close">ปิดรับ {displayClose}</p> : null}
          </div>
          <div className="htd-set__countdown" aria-live="polite">{displayTime}</div>
        </div>

        <div className="htd-set__grid">
          <section className="htd-set__panel" aria-label="ระบุตัวเลข">
            {resolvingPackage && (
              <div className="htd-set__alert" role="status">
                <i className="fas fa-spinner fa-spin" aria-hidden />
                กำลังโหลดราคาแพ็กเกจ...
              </div>
            )}

            {!resolvingPackage && packageHydrationFailed && (
              <div className="htd-set__alert htd-set__alert--error" role="alert">
                ไม่พบแพ็กเกจหวยชุดสำหรับรอบนี้ กรุณากลับไปเลือกแพ็กเกจใหม่
              </div>
            )}

            <div className="htd-set__warn">
              <span className="htd-set__warn-icon" aria-hidden>
                <img src={getAssetUrl('/img/warning.png')} alt="" />
              </span>
              <span>มีเลขปรับราคาจ่าย ให้ตรวจสอบราคาจ่ายทุกครั้ง</span>
            </div>

            <div className="htd-set__stats">
              <div className="htd-set__stat">
                <span className="htd-set__stat-label">{packageLabel}</span>
                <p className="htd-set__stat-value">
                  {resolvingPackage ? '...' : `฿ ${fixedPrice.toLocaleString()}`}
                </p>
              </div>
              <div className="htd-set__stat">
                <span className="htd-set__stat-label">ส่วนลด</span>
                <p className="htd-set__stat-value">{fixedDiscount}%</p>
              </div>
            </div>

            <h2 className="htd-set__panel-title">ระบุตัวเลข</h2>

            <div className="htd-set__digits" aria-label="ช่องกรอกเลข 4 หลัก">
              {numbers.map((number, index) => (
                <div key={index} className={`htd-set__digit${number ? ' is-filled' : ''}`}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={number}
                    onChange={(e) => handleNumberInput(index, e.target.value)}
                    maxLength={1}
                    aria-label={`หลักที่ ${index + 1}`}
                  />
                </div>
              ))}
            </div>

            <div className="htd-set__keypad">
              <div className="htd-set__keys">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <button
                    key={digit}
                    type="button"
                    className="htd-set__key"
                    onClick={() => handleNumberButton(String(digit))}
                  >
                    {digit}
                  </button>
                ))}
                <button
                  type="button"
                  className="htd-set__key htd-set__key--zero"
                  onClick={() => handleNumberButton('0')}
                >
                  0
                </button>
              </div>
              <div className="htd-set__side-keys">
                <button type="button" className="htd-set__key htd-set__key--action htd-set__key--back" onClick={handleBackspace} aria-label="ลบเลข">
                  <i className="fas fa-backspace" aria-hidden />
                </button>
                <button type="button" className="htd-set__key htd-set__key--action htd-set__key--random" onClick={handleRandom}>
                  สุ่มเลข
                </button>
                <button type="button" className="htd-set__key htd-set__key--action htd-set__key--clear" onClick={handleClear}>
                  ล้างเลข
                </button>
                <button type="button" className="htd-set__key htd-set__key--action htd-set__key--change" onClick={goBackToTerms}>
                  เปลี่ยนราคา
                </button>
              </div>
            </div>
          </section>

          <section className="htd-set__panel" aria-label="จำนวนชุดและส่งโพย">
            <h2 className="htd-set__panel-title">
              <i className="fas fa-clipboard-list me-2" aria-hidden />
              ใส่จำนวนชุด
            </h2>

            <div className="htd-set__qty-row">
              <div className="htd-set__stepper">
                <button type="button" onClick={() => handleSetCountDelta(-1)} aria-label="ลดจำนวนชุด">
                  <i className="fas fa-minus-circle" aria-hidden />
                </button>
                <input
                  name="number"
                  type="text"
                  autoComplete="off"
                  maxLength={5}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={setCountInput}
                  onChange={(e) => handleSetCountChange(e.target.value)}
                  onBlur={() => {
                    if (!setCountInput.trim()) {
                      setSetCountInput(String(DEFAULT_SET_COUNT))
                    }
                  }}
                  aria-label="จำนวนชุด"
                />
                <button type="button" onClick={() => handleSetCountDelta(1)} aria-label="เพิ่มจำนวนชุด">
                  <i className="fas fa-plus-circle" aria-hidden />
                </button>
              </div>

              <div className="htd-set__presets" role="group" aria-label="จำนวนชุดด่วน">
                {PRESET_SETS.map((count) => (
                  <button
                    key={count}
                    type="button"
                    className={`htd-set__preset${setCount === count ? ' is-active' : ''}`}
                    onClick={() => handleSetSelection(count)}
                  >
                    {count} ชุด
                  </button>
                ))}
              </div>
            </div>

            <div className="htd-set__stats">
              <div className="htd-set__stat htd-set__stat--credit">
                <span className="htd-set__stat-label">ยอดเครดิตคงเหลือ</span>
                <p className="htd-set__stat-value">
                  ฿ {isLoadingWallet ? '...' : walletService.formatCurrency(walletBalance)}
                </p>
              </div>
              <div className="htd-set__stat htd-set__stat--total">
                <span className="htd-set__stat-label">รวมยอดแทง</span>
                <p className="htd-set__stat-value">฿ {walletService.formatCurrency(totalBet)}</p>
              </div>
            </div>

            <div className="htd-set__hint">
              ระบบจะใช้เลข 4 หลักแรกของชุดที่กรอกส่งแทงด้วยแพ็กเกจที่เลือก และคิดยอดรวมตามจำนวนชุดที่ระบุ
            </div>

            <div className="htd-set__actions">
              <button
                type="button"
                className="htd-set__btn htd-set__btn--submit"
                disabled={!canSubmit}
                onClick={handleSubmitBet}
              >
                <i className="fas fa-paper-plane" aria-hidden />
                {submitting ? 'กำลังส่ง...' : 'ส่งโพย'}
              </button>
              <button type="button" className="htd-set__btn htd-set__btn--clear" onClick={handleClear}>
                <i className="fas fa-trash-alt" aria-hidden />
                ล้างรายการ
              </button>
            </div>

            <div className="htd-set__payout">
              <h3 className="htd-set__payout-title">ราคาจ่าย</h3>
              <div className="htd-set__payout-panel">
                <table className="htd-set__table">
                  <thead>
                    <tr>
                      <th scope="col">ชนิด</th>
                      <th scope="col" className={activePackageColumn === 'large' ? 'is-active' : undefined}>ชุดใหญ่</th>
                      <th scope="col" className={activePackageColumn === 'medium' ? 'is-active' : undefined}>ชุดกลาง</th>
                      <th scope="col" className={activePackageColumn === 'small' ? 'is-active' : undefined}>ชุดเล็ก</th>
                    </tr>
                  </thead>
                  <tbody>
                    {setPayoutRows.map((row) => (
                      <tr key={row.label}>
                        <th scope="row">
                          <span className="htd-set__table__type">{row.label}</span>
                        </th>
                        <td className={activePackageColumn === 'large' ? 'is-active' : undefined}>
                          <span className="htd-set__table__amount">{row.large}</span>
                        </td>
                        <td className={activePackageColumn === 'medium' ? 'is-active' : undefined}>
                          <span className="htd-set__table__amount">{row.medium}</span>
                        </td>
                        <td className={activePackageColumn === 'small' ? 'is-active' : undefined}>
                          <span className="htd-set__table__amount">{row.small}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </div>

      <RulesGuideModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        scope={round ? {
          huayId: round.huayId,
          huayName: round.huayName,
          huayTypeId: round.huayTypeId,
          huayTypeName: round.huayTypeName,
        } : undefined}
        fallbackRules={(
          <>
            <p>- ระบบเปิด-ปิดแทงตามเวลาที่ได้แจ้งไว้ และบริษัทสามารถพิจารณาปิดแทงก่อนกำหนดได้</p>
            <p>- ทางบริษัทสามารถคืนเลขได้ทุกกรณี แต่จะคืนก่อนที่จะออกผลรางวัล</p>
            <p>- คืนโพยได้ภายใน {formatCustomerSlipCancelWindowText(cancelWindowMinutes)}หลังจากที่แทง</p>
            <p>- ราคาจ่ายและส่วนลดขึ้นอยู่กับแพ็กเกจที่เลือก โปรดตรวจสอบก่อนส่งโพยทุกครั้ง</p>
            <p>- หวยชุดจะใช้อ้างอิงผลเลข 4 หลักของรอบนั้น และ 1 ชุดรับรางวัลใหญ่ที่สุดเพียงรางวัลเดียว</p>
          </>
        )}
        fallbackGuides={(
          <>
            <p className="text-center"><strong><u>วิธีการดูผลรางวัล "{displayTitle}"</u></strong></p>
            <p className="text-center">ใช้รางวัลเลข 4 หลักของหวยชุด เช่น 6089</p>
            <p className="mb-4 text-center"><strong><u>การตรวจผลรางวัล</u></strong></p>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <tbody>
                  <tr><td style={{ width: '100px' }}>4 ตัวบน</td><td>6089</td></tr>
                  <tr><td>3 ตัวตรง</td><td>089</td></tr>
                  <tr><td>4 ตัวโต๊ด</td><td>6089 สลับตำแน่งทั้ง 4 หลัก</td></tr>
                  <tr><td>3 ตัวโต๊ด</td><td>089 098 809 890 908 980</td></tr>
                  <tr><td>2 ตัวหน้า</td><td>60</td></tr>
                  <tr><td>2 ตัวหลัง</td><td>89</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-center">**จำกัดสิทธิ์ 1 ชุดรับรางวัลใหญ่ที่สุด รางวัลเดียว**</p>
          </>
        )}
      />
    </>
  )
}

export default ModernLobbyHuaySetLottoGamePage
